<template>
  <EditorPopover ref="popover" @cancel="cancelEditing">

    <TabbedBox>
      <template v-slot:tablist>
        <Tab :first="true" tabid="tab-panel" ref="tabPanel">
          <i class="far fa-draw-polygon"></i> Panel
        </Tab>
        <Tab tabid="tab-tools" v-if="isShapeEditable">
          <i class="far fa-tools"></i> Panel Tools
        </Tab>
        <Tab tabid="tab-info">
          <i class="far fa-info-circle"></i> Info
        </Tab>
        <Tab tabid="tab-splits" v-if="isStockLinked">
          <i class="far fa-border-all"></i> Splits
        </Tab>
        <Tab tabid="tab-developer" v-if="isDeveloperUser" ref="developerTab">
          <i class="far fa-tools"></i> Developer
        </Tab>
      </template>

      <template v-slot:tabcontent>
        <TabPage :first="true" tabid="tab-panel">
          <div class="row" v-if="itemData">
            <div class="col-6">
              <EditorPopoverDataRow label="Stock">{{itemStockData.name}}</EditorPopoverDataRow>
              <EditorPopoverDataRow label="Price Group">{{itemPriceGroupData.name}}</EditorPopoverDataRow>
              <EditorPopoverDataRow label="Mark">{{itemData.mark}}</EditorPopoverDataRow>
              <EditorPopoverDataRow label="Face Side Up" v-if="isStockUsingFaces">{{formatLookupName(itemData.stockFaceSideUpType, StockFaceTypeLookup)}}</EditorPopoverDataRow>
              <EditorPopoverDataRow label="Status" v-if="isShapeNCRLocked"><span class="text-danger">NCR Not Selected</span></EditorPopoverDataRow>
              <EditorPopoverDataRow label="Status" v-if="isShapeCancelled"><span class="text-danger">Cancelled Item</span></EditorPopoverDataRow>
              <EditorPopoverDataRow label="Status" v-if="isShapeInvoiced"><span class="text-danger">Invoiced Item</span></EditorPopoverDataRow>
              <EditorPopoverDataRow label="Status" v-if="isShapeDespatched"><span class="text-danger">Despatched Item</span></EditorPopoverDataRow>
            </div>
            <div class="col-6">
              <EditorPopoverDataRow label="Thickness">{{itemStockData.thickness}}mm</EditorPopoverDataRow>
              <EditorPopoverDataRow label="Quantity">{{itemData.quantity}}</EditorPopoverDataRow>
              <EditorPopoverDataRow label="Unit Price">{{itemData.unitPrice | formatCurrency}}</EditorPopoverDataRow>
              <EditorPopoverDataRow label="Grain" v-if="isStockUsingGrain">{{formatLookupName(itemData.grainDirection, StockGrainDirectionTypeLookup)}}</EditorPopoverDataRow>
            </div>
          </div>

          <EditorPopoverButtonList>
            <EditorPopoverButton v-if="isShapeEditable && isRemoveAvailable" title="Remove" type="remove" icon="fa-trash-alt" @click="popoverActionButtonClick('remove')" />
            <EditorPopoverButton v-if="isShapeEditable" title="Reset" type="action" icon="fa-times-square" @click="popoverActionButtonClick('resetPanel')" />
            <EditorPopoverButton v-if="isShapeEditable && isCloneAvailable" title="Clone" type="action" icon="fa-plus-square" @click="popoverActionButtonClick('clone')" />
            <EditorPopoverButton v-if="isShapeEditable && isCreateLibraryShapeAvailable" title="Create Library Shape" type="action" icon="fa-layer-plus" @click="popoverActionButtonClick('createLibraryShape')" />
            <EditorPopoverButton v-if="isShapeEditable" title="Update" type="edit" icon="fa-pencil" @click="popoverActionButtonClick('update')" />
          </EditorPopoverButtonList>
        </TabPage>

        <TabPage tabid="tab-tools">
          <div class="row no-gutters">
            <div class="col-3 px-1">
              <EditorPopoverButton v-if="isShapeEditable && isPanelToolAvailable" title="Rotate" class="mb-2 w-100" type="action" icon="fa-undo-alt fa-flip-horizontal" @click="popoverActionButtonClick('rotatePanel')" /><br />
            </div>
            <div class="col-3 px-1">
              <EditorPopoverButton v-if="isShapeEditable && isPanelToolAvailable" title="Mirror Horz" class="mb-2 w-100" type="action" icon="fa-arrows-h" @click="popoverActionButtonClick('mirrorXPanel')" /><br />
              <EditorPopoverButton v-if="isShapeEditable && isPanelToolAvailable" title="Mirror Vert" class="mb-2 w-100" type="action" icon="fa-arrows-v" @click="popoverActionButtonClick('mirrorYPanel')" /><br />
            </div>
            <div class="col-3 px-1">
              <EditorPopoverButton v-if="isShapeEditable && isPanelToolAvailable" title="Expand" class="mb-2 w-100" type="action" icon="fa-expand" @click="popoverActionButtonClick('expandPanel')" /><br />
              <EditorPopoverButton v-if="isShapeEditable && isPanelToolAvailable" title="Shrink" class="mb-2 w-100" type="action" icon="fa-compress" @click="popoverActionButtonClick('shrinkPanel')" />
            </div>
            <div class="col-3 px-1">
              <EditorPopoverButton v-if="isShapeEditable && isPanelToolAvailable" title="Snap Points" class="mb-2 w-100" type="action" icon="fa-vector-square" @click="popoverActionButtonClick('snapPointsPanel')" /><br />
              <EditorPopoverButton v-if="isShapeEditable && isPanelToolAvailable" title="Cleanup Points" class="mb-2 w-100" type="action" icon="fa-broom" @click="popoverActionButtonClick('cleanupPanel')" /><br />
              <EditorPopoverButton v-if="isShapeEditable && isPanelToolAvailable" title="Correct Rotation" class="mb-2 w-100" type="action" icon="fa-ruler-combined" @click="popoverActionButtonClick('correctRotationPanel')" /><br />
            </div>
          </div>
        </TabPage>

        <TabPage tabid="tab-info">
          <div class="row" v-if="shape">
            <div class="col-6">
              <EditorPopoverDataRow label="Overall Height">{{shapeInfo.height}}mm</EditorPopoverDataRow>
              <EditorPopoverDataRow label="Overall Width">{{shapeInfo.width}}mm</EditorPopoverDataRow>
              <EditorPopoverDataRow label="Panel Area">{{formatDecimal(shapeInfo.area, 2)}} m&sup2;</EditorPopoverDataRow>
              <EditorPopoverDataRow label="Panel Weight">{{formatDecimal(shapeInfo.weight, 2)}} Kg</EditorPopoverDataRow>
              <EditorPopoverDataRow label="Perimeter">{{formatDecimal(shapeInfo.perimeter, 2)}} mm</EditorPopoverDataRow>
            </div>
            <div class="col-6">              
              <EditorPopoverDataRow label="Straight Cuts">{{shapeInfo.straightCutCount}}</EditorPopoverDataRow>
              <EditorPopoverDataRow label="Corner Cutouts">{{shapeInfo.cornerCutoutCount}}</EditorPopoverDataRow>
              <EditorPopoverDataRow label="Edge Cutouts">{{shapeInfo.edgeCutoutCount}}</EditorPopoverDataRow>
              <EditorPopoverDataRow label="Center Cutouts">{{shapeInfo.centerCutoutCount}}</EditorPopoverDataRow>
              <EditorPopoverDataRow label="Splits">{{shapeInfo.splitCount}}</EditorPopoverDataRow>
            </div>
          </div>
        </TabPage>

        <TabPage tabid="tab-splits">
          <div v-if="glassSplitList.length > 0">
            <div class="mb-2"><strong>{{shapeInfo.splitCount}} splits</strong> to generate the following panels:</div>

            <div class="row pl-3" v-for="(splitText, index) of glassSplitList" :key="index">
              <div class="col-8">
                <EditorPopoverDataRow :label="`Panel #${index+1}`">{{splitText}}</EditorPopoverDataRow>
              </div>
            </div>
          </div>
          <div v-if="glassSplitList.length == 0" class="text-muted text-center">
            No splits in sheet
          </div>
        </TabPage>

        <TabPage tabid="tab-developer" v-if="isDeveloperUser">
          <div class="row">
            <div class="col-6">
              <EditorPopoverButton class="mb-2" title="Log Cutting Segs" type="action" icon="fa-code" @click="developerLogCuttingData" /><br />
              <EditorPopoverButton class="mb-2" title="Log Runouts" type="action" icon="fa-code" @click="developerLogRunoutData" /><br />
              <EditorPopoverButton class="mb-2" title="Log Edgework Charge" type="action" icon="fa-code" @click="developerLogEdgeworkChargeData" /><br />
              <EditorPopoverButton class="mb-2" title="Log Seg Pos" type="action" icon="fa-code" @click="developerLogSegmentPositionData" /><br />
            </div>
            <div class="col-6">
              <EditorPopoverButton class="mb-2" title="Log Edgework Cutting" type="action" icon="fa-code" @click="developerLogEdgeworkCuttingData" /><br />
              <EditorPopoverButton class="mb-2" title="Log Cutting Line List" type="action" icon="fa-code" @click="developerLogCuttingLineListData" /><br />
              <EditorPopoverButton class="mb-2" title="Log Segments" type="action" icon="fa-code" @click="developerLogSegmentData" /><br />
              <EditorPopoverButton class="mb-2" title="Log Seg Arcs" type="action" icon="fa-code" @click="developerLogSegmentArcData" /><br />
            </div>
          </div>
        </TabPage>
      </template>
    </TabbedBox>  
    
  </EditorPopover>
</template>



<script>
import _ from 'lodash'
import Utils from '@/utils/utils'
import EditorPopover from '@glass-designer/components/editorPopover'
import EditorPopoverMixin from '@glass-designer/mixins/editorPopover'
import EditorPopoverDataRow from '@glass-designer/components/editorPopoverDataRow'
import EditorKeypad from '@glass-designer/components/editorKeypad'
import MathsUtils from '@/utils/mathsUtils'

import EditorPopoverButtonList from '@glass-designer/components/popover/editorPopoverButtonList'
import EditorPopoverButton from '@glass-designer/components/popover/editorPopoverButton'

import { LibraryItemShapeTypes } from '@glass-designer/types'
import { OrderEditorCutoutTypes, StockFaceTypeLookup, StockGrainDirectionTypeLookup } from '@/store/types'

export default {
  name: 'EditorShapePopover',
  mixins: [EditorPopoverMixin],
  components: {
    EditorPopover,
    EditorPopoverDataRow,
    EditorPopoverButtonList,
    EditorPopoverButton
  },
  props: {
    editingEnabled: {
      type: Boolean,
      default: true
    },
  },
  data() {
    return {
      toolInfoText: '',
      name: '',
      shape: null,
      itemData: null,
      isRemoveAvailable: true,
      isCloneAvailable: true,
      shapeInfo: {
        width: 0,
        height: 0,
        area: 0,
        weight: 0,
        thickness: 0,
        perimeter: 0,
        straightCutCount: 0,
        cornerCutoutCount: 0,
        edgeCutoutCount: 0,
        centerCutoutCount: 0,
        splitCount: 0
      }
    }
  },
  computed: {
    itemStockData() {
      if(this.itemData == null)
        return {}
      return this.itemData.stock || {}
    },
    itemPriceGroupData() {
      if(this.itemData == null)
        return {}
      return this.itemData.stockPriceGroup || {}
    },
    isCreateLibraryShapeAvailable() {
      if(this.itemData == null)
        return false

      // Don't bother with creating library shape from stock-only panels
      // Only non-stock linked panels
      if(this.itemPriceGroupData && !this.itemPriceGroupData.isStockLinked)
        return true

      return false
    },
    isStockLinked() {
      return this.itemPriceGroupData.isStockLinked
    },
    isPanelToolAvailable() {
      // Don't rotate if stock linked
      if(this.isStockLinked)
        return false
      return true    
    },
    glassSplitList() {
      if(!this.shape)
        return []
      let splitRectangles = this.shape.calculateSplittingRectangles()

      return splitRectangles.map(rectangle => {
        return `${Math.round(rectangle.max.y - rectangle.min.y)}mm x ${Math.round(rectangle.max.x - rectangle.min.x)}mm`
      })
    },
    isShapeNCRLocked() {
      if(this.shape)
        return this.shape.isNCRLocked()
      return false
    },
    isShapeCancelled() {
      if(this.shape)
        return this.shape.isCancelled()
      return false
    },
    isShapeInvoiced() {
      if(this.shape)
        return this.shape.isInvoiced()
      return false
    },
    isShapeDespatched() {
      if(this.shape)
        return this.shape.isDespatched()
      return false
    },
    isShapeEditable() {
      if(!this.editingEnabled)
        return false
      if(this.shape) {
        if(!this.shape.isItemEditable())
          return false
      }
      return true
    },
    isStockUsingFaces() {
      return !!this.itemStockData.isStockUsingFaces
    },
    isStockUsingGrain() {
      return !!this.itemStockData.isStockUsingGrain
    }
  },
  watch: {
    
  },
  created() {
    this.StockFaceTypeLookup = StockFaceTypeLookup
    this.StockGrainDirectionTypeLookup = StockGrainDirectionTypeLookup
  },
  methods: {
    setup(editor, options) {
      this.clearOptions()

      this.name = options.name || 'Glass Item'
      this.itemData = options.itemData || {}
      this.shape = options.shape || null
      this.isRemoveAvailable = !!options.isRemoveAvailable
      this.isCloneAvailable = !!options.isCloneAvailable

      if(this.shape) {
        let cuttingLineList = this.shape.calculateCuttingLineList()
        let cncCutoutList = this.shape.calculateCNCCutoutList()

        let straightLineCutCount = cuttingLineList.filter(cutLine => !cutLine.isOnBounds).length

        this.shapeInfo.width = this.shape.calculateWidth()
        this.shapeInfo.height = this.shape.calculateHeight()
        this.shapeInfo.thickness = this.shape.getThickness()
        this.shapeInfo.area = Utils.convertMillimetreSqToMetreSq(this.shape.calculateFinalArea())
        this.shapeInfo.weight = this.shape.calculateFinalWeight()
        this.shapeInfo.perimeter = this.shape.calculatePerimeter()
        this.shapeInfo.straightCutCount = straightLineCutCount
        this.shapeInfo.cornerCutoutCount = cncCutoutList.filter(cutout => cutout.type == OrderEditorCutoutTypes.cornerCutOut).length
        this.shapeInfo.edgeCutoutCount = cncCutoutList.filter(cutout => cutout.type == OrderEditorCutoutTypes.edgeCutOut).length
        this.shapeInfo.centerCutoutCount = cncCutoutList.filter(cutout => cutout.type == OrderEditorCutoutTypes.centerCutOut).length
        this.shapeInfo.splitCount = this.shape.getSplitLineList().length
      }

      // Setup callbacks next tick so they don't get called when resetting values
      this.callbackContext = options.context
      this.updateCallback = options.updateCallback
      this.actionCallback = options.actionCallback
      this.applyCallback = options.applyCallback
      this.cancelCallback = options.cancelCallback

      this.$nextTick(() => {
        this.$refs.tabPanel.show()
      })
    },
    clearOptions() {
      this.itemData = null

      this.callbackContext = null
      this.updateCallback = null
      this.actionCallback = null
      this.applyCallback = null
      this.cancelCallback = null
    },
    displayToolPopover(containerEl, editor, displayOptions) {
      this.$refs.popover.displayToolPopover(containerEl, editor, displayOptions)
    },
    viewportUpdated(containerEl, editor) {
    },
    hide() {
      if(this.cancelCallback) {
        // Reset cancel CB so it only is called once when hiding popovers
        let cancelCB = this.cancelCallback
        this.cancelCallback = null

        cancelCB(this.callbackContext, false)
      }

      this.clearOptions()
      this.$refs.popover.hide()
    },
    cancelEditing() {
      if(this.cancelCallback)
        this.cancelCallback(this.callbackContext, true)

      this.clearOptions()
      this.hide()
    },
    onShortcutKey(code) {
      switch(code) {
        case 'Escape':
          this.cancelEditing()
          break
        case 'Delete':
        case 'Backspace':
          if(this.isShapeEditable && this.isRemoveAvailable)
            this.popoverActionButtonClick('remove')
          break
      }
    },

    // Developer tools
    developerLogCuttingData() {
      var shapeData = this.shape.generateShapeData()

      var cuttingList = shapeData.cuttingEdgeworkList.filter(cutSeg => cutSeg.isCuttingSegment)

      console.log(`Transformed Cutting Segments:`)
      console.log(JSON.stringify(cuttingList, null, 2))
    },
    developerLogRunoutData() {
      var runoutData = this.shape.calculateRunoutList()

      console.log('GlassShape::calculateRunoutList():')
      console.log(JSON.stringify(runoutData, null, 2))
    },
    developerLogEdgeworkChargeData() {
      var edgeworkEdgeData = this.shape.calculateEdgeworkEdgeList()

      console.log(`GlassShape::calculateEdgeworkEdgeList():`)
      console.log(JSON.stringify(edgeworkEdgeData.edgeworkChargeList, null, 2))
    },
    developerLogEdgeworkCuttingData() {
      var edgeworkCuttingData = this.shape.calculateCuttingEdgeworkList()

      console.log(`GlassShape::calculateCuttingEdgeworkList():`)
      console.log(JSON.stringify(edgeworkCuttingData, null, 2))
    },
    developerLogCuttingLineListData() {
      var cuttingLineList = this.shape.calculateCuttingLineList()

      console.log(`GlassShape::calculateCuttingLineList():`)
      console.log(JSON.stringify(cuttingLineList, null, 2))
    },
    developerLogSegmentData() {
      var segmentList = this.shape.segmentList

      console.log(`GlassShape::developerLogSegmentData():`)
      console.log(JSON.stringify(segmentList, null, 2))
    },
    developerLogSegmentPositionData() {
      var segmentList = this.shape.segmentList

      console.log(`GlassShape::developerLogSegmentPositionData():`)
      var outputData = segmentList.map((segment, index) => {
        return {
          index: index,
          type: segment.type,
          startPosition: segment.startPosition,
          endPosition: segment.endPosition
        }
      })
      console.log(JSON.stringify(outputData, null, 2))
    },
    developerLogSegmentArcData() {
      var segmentList = this.shape.segmentList

      console.log(`GlassShape::developerLogSegmentArcData():`)
      var outputData = segmentList.filter(segment => segment.type == 'arc').map((segment, index) => {
        return {
          index: index,
          type: segment.type,
          arcCenterPosition: segment.arcCenterPosition,
          arcRadius: segment.arcRadius,
          arcStartAngle: segment.arcStartAngle,
          arcEndAngle: segment.arcEndAngle,
          arcClockwise: segment.arcClockwise
        }
      })
      console.log(JSON.stringify(outputData, null, 2))
    }
  }
}
</script>